import React, { useCallback, useEffect, useState } from 'react';
import {
  Sparkles, Video, Camera, Share2, Send, ExternalLink, CheckCircle2,
  XCircle, Clock, Loader2, Ticket, Copy,
} from 'lucide-react';
import { api } from '../lib/api';
import { useAuth } from '../contexts/AuthContext';
import { toast } from '../hooks/use-toast';

const PLATFORMS = [
  { id: 'tiktok',    label: 'TikTok',    accent: '#000000', badge: '🎵' },
  { id: 'instagram', label: 'Instagram', accent: '#e1306c', badge: '📷' },
  { id: 'youtube',   label: 'YouTube',   accent: '#ff0000', badge: '▶️' },
  { id: 'twitter',   label: 'X / Twitter', accent: '#000000', badge: '𝕏' },
  { id: 'facebook',  label: 'Facebook',  accent: '#1877f2', badge: 'f' },
  { id: 'other',     label: 'Other',     accent: '#9d3dff', badge: '🌐' },
];
const ENTRIES_PER_APPROVAL = 100;

function StatusPill({ status }) {
  if (status === 'approved') {
    return (
      <span className="inline-flex items-center gap-1 text-[10px] font-black uppercase tracking-wider px-2 py-0.5 rounded-full bg-emerald-500 text-white border-2 border-[#0c5132] shadow-[0_2px_0_#0c5132]">
        <CheckCircle2 className="h-3 w-3" strokeWidth={3} /> +{ENTRIES_PER_APPROVAL} Entries
      </span>
    );
  }
  if (status === 'rejected') {
    return (
      <span className="inline-flex items-center gap-1 text-[10px] font-black uppercase tracking-wider px-2 py-0.5 rounded-full bg-rose-500 text-white border-2 border-[#7f1d1d] shadow-[0_2px_0_#7f1d1d]">
        <XCircle className="h-3 w-3" strokeWidth={3} /> Rejected
      </span>
    );
  }
  return (
    <span className="inline-flex items-center gap-1 text-[10px] font-black uppercase tracking-wider px-2 py-0.5 rounded-full bg-yellow-300 text-[#7a4c00] border-2 border-[#7a4c00] shadow-[0_2px_0_#7a4c00]">
      <Clock className="h-3 w-3" strokeWidth={3} /> Pending review
    </span>
  );
}

export default function CreatorsSection() {
  const { isAuthed } = useAuth();
  const [platform, setPlatform] = useState('tiktok');
  const [url, setUrl] = useState('');
  const [note, setNote] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [submissions, setSubmissions] = useState([]);
  const [bonus, setBonus] = useState({ bonus_entries: 0, approved_count: 0, pending_count: 0 });
  const [loading, setLoading] = useState(false);

  const load = useCallback(async () => {
    if (!isAuthed) return;
    setLoading(true);
    try {
      const [subs, b] = await Promise.all([
        api.listMyCreatorPosts().catch(() => []),
        api.getMyCreatorBonus().catch(() => ({ bonus_entries: 0, approved_count: 0, pending_count: 0 })),
      ]);
      setSubmissions(subs || []);
      setBonus(b);
    } finally {
      setLoading(false);
    }
  }, [isAuthed]);

  useEffect(() => { load(); }, [load]);

  const submit = async () => {
    const trimmed = url.trim();
    if (!/^https?:\/\//i.test(trimmed)) {
      toast({ title: 'Paste the full link', description: 'It should start with https://' });
      return;
    }
    setSubmitting(true);
    try {
      const created = await api.submitCreatorPost({ platform, url: trimmed, note: note.trim() });
      setSubmissions((prev) => [created, ...prev]);
      setBonus((b) => ({ ...b, pending_count: (b.pending_count || 0) + 1 }));
      setUrl('');
      setNote('');
      toast({ title: 'Submitted!', description: 'The devs will review your post and drop 100 bonus entries in your account.' });
    } catch (e) {
      toast({ title: 'Could not submit', description: e.message });
    } finally {
      setSubmitting(false);
    }
  };

  const copyShareLink = () => {
    const link = typeof window !== 'undefined' ? window.location.origin : 'https://sprite-vault.com';
    navigator.clipboard?.writeText(link);
    toast({ title: 'Link copied!', description: 'Paste it in your TikTok caption, Instagram bio, or YouTube description.' });
  };

  return (
    <section className="max-w-7xl mx-auto px-3 sm:px-6 mt-6" data-testid="creators-section">
      <div
        className="relative rounded-2xl sm:rounded-3xl border-[3px] border-[#3a1064] overflow-hidden shadow-[0_8px_0_#3a1064,0_24px_60px_-12px_rgba(0,0,0,0.4)]"
      >
        {/* Header banner */}
        <div
          className="relative p-3 sm:p-6 md:p-8"
          style={{
            background:
              'radial-gradient(circle at 0% 0%, #ff2bd6 0%, transparent 40%),' +
              'radial-gradient(circle at 100% 100%, #ffd900 0%, transparent 40%),' +
              'linear-gradient(135deg, #3a1064 0%, #5b1ca8 100%)',
          }}
        >
          <div className="flex items-start gap-3 sm:gap-4 flex-wrap">
            <div className="h-11 w-11 sm:h-14 sm:w-14 rounded-xl sm:rounded-2xl bg-[#ffd900] border-[3px] border-[#1a0440] flex items-center justify-center -rotate-6 shadow-[0_4px_0_#1a0440] shrink-0">
              <Video className="h-6 w-6 sm:h-7 sm:w-7 text-[#3a1064]" strokeWidth={3} />
            </div>
            <div className="min-w-0 flex-1">
              <div className="text-yellow-300 font-black text-lg sm:text-2xl md:text-3xl leading-tight">
                Creator Rewards
              </div>
              <p className="text-white/90 font-bold text-xs sm:text-sm md:text-base mt-1 leading-tight">
                Post a <span className="bg-[#ff2bd6] px-1.5 py-0.5 rounded text-white">TikTok</span>, <span className="bg-[#e1306c] px-1.5 py-0.5 rounded text-white">Instagram Reel</span>, <span className="bg-[#ff0000] px-1.5 py-0.5 rounded text-white">YouTube Short</span> or Facebook post about Sprite Vault →
                get <span className="bg-yellow-300 text-[#3a1064] px-1.5 py-0.5 rounded">+100 lottery entries</span> per approved post.
              </p>
            </div>
          </div>

          {isAuthed && (
            <div className="mt-4 grid grid-cols-3 gap-2 sm:gap-3">
              <div className="rounded-lg sm:rounded-xl p-2 sm:p-3 bg-[#ffd900] border-2 border-[#1a0440] shadow-[0_3px_0_#1a0440]">
                <div className="text-[9px] sm:text-[10px] font-black uppercase tracking-wider text-[#3a1064]/70">Bonus Entries</div>
                <div className="flex items-center gap-1 mt-0.5">
                  <Ticket className="h-4 w-4 sm:h-5 sm:w-5 text-[#3a1064]" />
                  <div className="text-xl sm:text-3xl font-black text-[#3a1064] leading-none">{bonus.bonus_entries}</div>
                </div>
              </div>
              <div className="rounded-lg sm:rounded-xl p-2 sm:p-3 bg-emerald-400 border-2 border-[#0c5132] shadow-[0_3px_0_#0c5132]">
                <div className="text-[9px] sm:text-[10px] font-black uppercase tracking-wider text-emerald-950">Approved</div>
                <div className="text-xl sm:text-3xl font-black text-emerald-950 leading-none mt-0.5">{bonus.approved_count}</div>
              </div>
              <div className="rounded-lg sm:rounded-xl p-2 sm:p-3 bg-yellow-200 border-2 border-[#7a4c00] shadow-[0_3px_0_#7a4c00]">
                <div className="text-[9px] sm:text-[10px] font-black uppercase tracking-wider text-[#7a4c00]">Pending</div>
                <div className="text-xl sm:text-3xl font-black text-[#7a4c00] leading-none mt-0.5">{bonus.pending_count}</div>
              </div>
            </div>
          )}
        </div>

        {/* Body */}
        <div className="p-3 sm:p-5 md:p-6 bg-white space-y-4">
          {/* How it works */}
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-2 sm:gap-3">
            <StepCard n="1" icon={Camera} title="Make it" desc="Film a TikTok showing off your Sprite Vault. Show your rare drops, trade wins, or how you use the tracker." />
            <StepCard n="2" icon={Share2} title="Post it" desc="Share on TikTok, Reels, Shorts, X, or a Facebook group. Add our link in the caption or bio." accent="#ff2bd6" />
            <StepCard n="3" icon={Ticket} title="Get entries" desc="Paste the link below. Once approved, +100 bonus entries drop straight into your V-Bucks drawing." accent="#00b8ff" />
          </div>

          {/* Share link helper */}
          <div className="flex items-center gap-2 rounded-xl border-2 border-[#3a1064]/20 bg-yellow-50 p-2.5">
            <div className="min-w-0 flex-1 truncate font-black text-sm text-[#3a1064]" data-testid="share-link-display">
              {typeof window !== 'undefined' ? window.location.origin : 'https://sprite-vault.com'}
            </div>
            <button
              data-testid="copy-share-link-btn"
              onClick={copyShareLink}
              className="h-9 px-3 rounded-lg border-2 border-[#3a1064] bg-white hover:bg-yellow-100 text-[#3a1064] font-black text-xs shadow-[0_2px_0_#3a1064] flex items-center gap-1.5 shrink-0"
            >
              <Copy className="h-3.5 w-3.5" /> Copy
            </button>
          </div>

          {/* Submit form */}
          {isAuthed ? (
            <div className="rounded-xl border-[3px] border-[#3a1064] p-3 sm:p-4 bg-yellow-50 shadow-[0_4px_0_#3a1064]">
              <div className="font-black text-[#3a1064] text-sm sm:text-base flex items-center gap-2">
                <Send className="h-4 w-4 text-[#ff2bd6]" strokeWidth={3} /> Submit your post
              </div>

              {/* Platform pills */}
              <div className="mt-2 flex flex-wrap gap-1.5">
                {PLATFORMS.map((p) => {
                  const active = platform === p.id;
                  return (
                    <button
                      key={p.id}
                      data-testid={`creator-platform-${p.id}`}
                      onClick={() => setPlatform(p.id)}
                      className={`h-8 px-2.5 rounded-full text-[11px] font-black border-2 flex items-center gap-1 transition-all ${
                        active
                          ? 'bg-[#3a1064] text-yellow-300 border-[#3a1064] shadow-[0_2px_0_#1a0440]'
                          : 'bg-white text-[#3a1064] border-[#3a1064]/30 hover:border-[#3a1064]'
                      }`}
                    >
                      <span className="text-sm leading-none">{p.badge}</span>
                      {p.label}
                    </button>
                  );
                })}
              </div>

              <input
                data-testid="creator-url-input"
                value={url}
                onChange={(e) => setUrl(e.target.value)}
                placeholder="Paste the link to your post…"
                className="mt-3 w-full h-11 rounded-xl border-2 border-[#3a1064] px-3 font-bold text-[#3a1064] focus:outline-none focus:ring-2 focus:ring-[#ff2bd6]/40 bg-white"
                maxLength={500}
              />

              <input
                data-testid="creator-note-input"
                value={note}
                onChange={(e) => setNote(e.target.value)}
                placeholder="Optional note to the devs (what you posted, tag/hashtag used, etc.)"
                className="mt-2 w-full h-11 rounded-xl border-2 border-[#3a1064]/40 px-3 font-bold text-[#3a1064] text-sm focus:outline-none focus:ring-2 focus:ring-[#ff2bd6]/40 bg-white"
                maxLength={500}
              />

              <button
                data-testid="creator-submit-btn"
                onClick={submit}
                disabled={submitting || !url.trim()}
                className="mt-3 w-full h-11 rounded-xl border-[3px] border-[#3a1064] bg-[#ff2bd6] hover:bg-[#e119bf] disabled:opacity-40 text-white font-black shadow-[0_4px_0_#3a1064] flex items-center justify-center gap-2 uppercase tracking-wide"
              >
                {submitting ? <Loader2 className="h-4 w-4 animate-spin" /> : <><Sparkles className="h-4 w-4" strokeWidth={3} /> Submit for +100 entries</>}
              </button>
            </div>
          ) : (
            <div className="rounded-xl border-2 border-dashed border-[#3a1064]/40 p-4 text-center bg-yellow-50">
              <div className="font-black text-[#3a1064]">Sign in to submit your post</div>
              <div className="text-xs font-bold text-[#3a1064]/70 mt-1">
                Anyone can share the link — but you need an account for us to credit your +10 entries.
              </div>
            </div>
          )}

          {/* My submissions list */}
          {isAuthed && (
            <div>
              <div className="text-[11px] font-black uppercase tracking-[2px] text-[#3a1064]/60 mb-2">My Submissions</div>
              {loading && submissions.length === 0 && (
                <div className="text-center py-4"><Loader2 className="h-5 w-5 mx-auto animate-spin text-[#9d3dff]" /></div>
              )}
              {!loading && submissions.length === 0 && (
                <div className="text-center text-sm font-bold text-[#3a1064]/60 py-4">
                  No submissions yet — post a Sprite Vault video and drop the link above!
                </div>
              )}
              <div className="space-y-2">
                {submissions.map((s) => {
                  const p = PLATFORMS.find((x) => x.id === s.platform) || PLATFORMS[5];
                  return (
                    <div key={s.id} className="rounded-xl border-2 border-[#3a1064]/15 bg-white p-2.5 flex items-center gap-2.5 shadow-[0_2px_0_rgba(58,16,100,0.08)]">
                      <div className="h-9 w-9 rounded-lg border-2 border-[#3a1064] flex items-center justify-center shrink-0 text-lg font-black" style={{ background: p.accent === '#000000' ? '#000' : `${p.accent}15`, color: p.accent }}>
                        {p.badge}
                      </div>
                      <div className="min-w-0 flex-1">
                        <div className="flex items-center gap-1.5 flex-wrap">
                          <StatusPill status={s.status} />
                          <span className="text-[10px] font-black uppercase text-[#3a1064]/50">{p.label}</span>
                        </div>
                        <a
                          href={s.url}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-xs font-bold text-[#3a1064] hover:text-[#ff2bd6] flex items-center gap-1 truncate mt-0.5"
                        >
                          <ExternalLink className="h-3 w-3 shrink-0" /> <span className="truncate">{s.url}</span>
                        </a>
                        {s.review_note && (
                          <div className="mt-0.5 text-[11px] font-bold text-[#3a1064]/60 italic">Dev note: {s.review_note}</div>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          )}
        </div>
      </div>
    </section>
  );
}

function StepCard({ n, icon: Icon, title, desc, accent = '#ffd900' }) {
  return (
    <div className="rounded-xl border-2 border-[#3a1064] p-2.5 sm:p-3 bg-yellow-50 shadow-[0_3px_0_#3a1064]">
      <div className="flex items-center gap-1.5 mb-1">
        <span className="h-6 w-6 rounded-full bg-[#3a1064] text-yellow-300 flex items-center justify-center font-black text-xs shrink-0">{n}</span>
        <Icon className="h-4 w-4 text-[#3a1064]" strokeWidth={3} />
        <span className="font-black text-sm text-[#3a1064]" style={{ background: accent, padding: '0 4px', borderRadius: 4 }}>{title}</span>
      </div>
      <div className="text-[11px] sm:text-xs font-bold text-[#3a1064]/80 leading-snug">{desc}</div>
    </div>
  );
}
